import {
  INDUSTRY_TEXT_MAX_LENGTH,
  NAME_MAX_LENGTH,
  isAcceptedPreferenceText,
  type IndustryCode,
} from 'features/conversation-preferences/services/conversation-preferences-service';
import { requestOnboardingCompletion } from './onboarding-completion';
import { isIndustryCode } from './onboarding-industry';
import { formatOnboardingList } from './onboarding-script';
import type { SalutationGuess } from './onboarding-salutation';
import {
  buildOnboardingAnswerPrompt,
  type OnboardingAnswerField,
} from './onboarding-answer-prompt';

export interface OnboardingNameAnswer {
  givenName: string;
  familyName: string | null;
  address: 'formal' | 'informal' | 'unknown';
  /** Always unclear in informal mode or without a family name. */
  salutationGuess: SalutationGuess;
}

export interface OnboardingIndustryAnswer {
  code: IndustryCode;
  /** The user's industry, set only for `other`. */
  text?: string;
}

type OnboardingAnswer = {
  name: OnboardingNameAnswer;
  industry: OnboardingIndustryAnswer;
  team: string;
  tools: string;
};

const ADDRESSES = new Set(['formal', 'informal', 'unknown']);
const GUESSES = new Set(['frau', 'herr', 'unclear']);

/** A plain label the preference would store, with nothing that could open markdown or a template. */
const isPlainText = (value: unknown, maxLength: number): value is string =>
  isAcceptedPreferenceText(value, maxLength) && !/[*[\]{}]/.test(value);

/** Key lists compared in one fixed order, whatever order the model wrote them in. */
const sortedKeys = (keys: string[]) => [...keys].sort((a, b) => a.localeCompare(b)).join();

const expectedKeys = (field: OnboardingAnswerField, envelope: Record<string, unknown>) => {
  if (field === 'name') return ['address', 'family', 'field', 'salutationGuess', 'values'];
  if (field === 'industry' && Array.isArray(envelope.values) && envelope.values[0] === 'other')
    return ['field', 'text', 'values'];
  return ['field', 'values'];
};

/** Reuses a temporary backend session, never Buddy conversation history. Anything off-contract
 *  (shape, keys, lengths, characters) reads as no answer. */
export async function extractOnboardingAnswer<F extends OnboardingAnswerField>(
  field: F,
  response: string,
  language?: string,
  labels?: string[]
): Promise<OnboardingAnswer[F] | null> {
  const text = await requestOnboardingCompletion(
    buildOnboardingAnswerPrompt(field, response, language, labels)
  );
  try {
    const envelope = JSON.parse(text);
    if (
      !envelope ||
      Array.isArray(envelope) ||
      typeof envelope !== 'object' ||
      sortedKeys(Object.keys(envelope)) !== sortedKeys(expectedKeys(field, envelope)) ||
      envelope.field !== field ||
      !Array.isArray(envelope.values) ||
      !envelope.values.length ||
      (field !== 'tools' && envelope.values.length !== 1) ||
      envelope.values.length > 30
    )
      return null;
    const values: string[] = [];
    for (const value of envelope.values) {
      if (!isPlainText(value, field === 'name' ? NAME_MAX_LENGTH : 120)) return null;
      const trimmed = value.trim();
      if (!values.some((existing) => existing.toLowerCase() === trimmed.toLowerCase()))
        values.push(trimmed);
    }
    return parseValues(field, values, envelope) as OnboardingAnswer[F] | null;
  } catch {
    return null;
  }
}

function parseValues(
  field: OnboardingAnswerField,
  values: string[],
  envelope: Record<string, unknown>
): OnboardingAnswer[OnboardingAnswerField] | null {
  if (field === 'tools') return formatOnboardingList(values);
  if (field === 'team') return values[0];
  if (field === 'industry') {
    const code = values[0];
    if (!isIndustryCode(code)) return null;
    if (code !== 'other') return { code };
    if (!isPlainText(envelope.text, INDUSTRY_TEXT_MAX_LENGTH)) return null;
    return { code, text: envelope.text.trim() };
  }
  const { family, address, salutationGuess } = envelope;
  if (family !== null && !isPlainText(family, NAME_MAX_LENGTH)) return null;
  if (!ADDRESSES.has(address as string) || !GUESSES.has(salutationGuess as string))
    return null;
  const familyName = family === null ? null : family.trim();
  return {
    givenName: values[0],
    familyName,
    address: address as OnboardingNameAnswer['address'],
    // The guess only counts where it is used: Sie mode with a family name.
    salutationGuess:
      address === 'informal' || !familyName ? 'unclear' : (salutationGuess as SalutationGuess),
  };
}
